import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Input,
  IconButton,
  VStack,
  HStack,
  useColorModeValue,
  keyframes,
  Tooltip,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { LuSparkles, LuSend, LuX, LuChevronDown } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';

// Glow animation for when dashboard first opens
const glowPulse = keyframes`
  0% { 
    box-shadow: 0 0 5px rgba(99, 102, 241, 0.4), 0 0 10px rgba(99, 102, 241, 0.3), 0 0 15px rgba(99, 102, 241, 0.2);
  }
  50% { 
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.6), 0 0 20px rgba(99, 102, 241, 0.4), 0 0 30px rgba(99, 102, 241, 0.3);
  }
  100% { 
    box-shadow: 0 0 5px rgba(99, 102, 241, 0.4), 0 0 10px rgba(99, 102, 241, 0.3), 0 0 15px rgba(99, 102, 241, 0.2);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-2px); }
`;

// Dashboard-specific prompts based on the current page
const DASHBOARD_PROMPTS = {
  'total-reviews': [
    'Which business has the most reviews?',
    'Compare review counts across competitors',
    'Who is growing their review count fastest?',
  ],
  'gross-growth': [
    'Which business is growing reviews fastest?',
    'Show me growth trends over the last quarter',
    'Who has the best review momentum?',
  ],
  'sentiment': [
    'Which competitor has the best sentiment?',
    'What are customers complaining about most?',
    'Show positive sentiment trends',
  ],
  'market-trends': [
    'What were the biggest changes this month?',
    'Which businesses improved most?',
    'Summarize market trends for me',
  ],
  'web-traffic': [
    'Who has the best SEO performance?',
    'What keywords are competitors ranking for?',
    'Show me traffic trends',
  ],
  'business-insights': [
    'Give me a complete competitive overview',
    'Who is the market leader and why?',
    'What opportunities exist in this market?',
  ],
  'market-intelligence': [
    'Who are my top competitors?',
    'What makes top performers successful?',
    'Identify gaps in the market',
  ],
  'keyword-intelligence': [
    'Who has the most web traffic?',
    'Show me traffic trends over time',
    'Which business is gaining traffic?',
  ],
  'ads-intelligence': [
    'Who is spending the most on ads?',
    'What ad strategies are working?',
    'Compare advertising approaches',
  ],
  'playbook-goals': [
    'Help me set realistic goals',
    'What benchmarks should I aim for?',
    'Suggest improvement targets',
  ],
  'playbook-actions': [
    'What actions should I prioritize?',
    'Create an action plan for growth',
    'What quick wins are available?',
  ],
  'playbook-reading': [
    'Summarize what customers are saying',
    'Find reviews about specific topics',
    'What themes appear in negative reviews?',
  ],
  'favorites': [
    'How does my favorite compare to leaders?',
    'What improvements would help most?',
    'Compare performance over time',
  ],
  'localytics-score': [
    'Explain how the ranking is calculated',
    'Who has the best overall ranking?',
    'What drives better rankings?',
  ],
  'pricing': [
    'Compare pricing across competitors',
    'Who offers the best value?',
    'What price tier is most successful?',
  ],
  'content-templates': [
    'Suggest content for my business',
    'What messaging works best?',
    'Create a marketing campaign idea',
  ],
  'spyfu-keywords': [
    'What keywords should I target?',
    'Which keywords have high opportunity?',
    'Compare my keyword strategy to competitors',
  ],
  'home': [
    'Show me the top performers in my market',
    'What are customers complaining about?',
    'Generate a competitive analysis report',
  ],
  default: [
    'Ask me anything about your market',
    'What would you like to analyze?',
    'How can I help with your research?',
  ],
};

// Map routes to dashboard types
const getDashboardTypeFromPath = (pathname) => {
  if (pathname === '/home') return 'home';
  if (pathname.includes('/dashboards/')) {
    const type = pathname.replace('/dashboards/', '');
    return type;
  }
  if (pathname.includes('/playbook/')) {
    const page = pathname.replace('/playbook/', '');
    return `playbook-${page}`;
  }
  return 'default';
};

function FloatingChatButton({ onOpenChat }) {
  const location = useLocation();
  const [dashboardType, setDashboardType] = useState('default');
  const [prompts, setPrompts] = useState(DASHBOARD_PROMPTS.default);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [isGlowing, setIsGlowing] = useState(false);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const inputRef = useRef(null);
  const lastPathRef = useRef(location.pathname);

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const placeholderColor = useColorModeValue('gray.400', 'gray.500');
  const promptBg = useColorModeValue('gray.50', 'gray.700');
  const promptHoverBg = useColorModeValue('blue.50', 'blue.900');

  // Update dashboard type and prompts when route changes
  useEffect(() => {
    const type = getDashboardTypeFromPath(location.pathname);
    setDashboardType(type);
    setPrompts(DASHBOARD_PROMPTS[type] || DASHBOARD_PROMPTS.default);
    setCurrentPromptIndex(0);
    
    // Trigger glow animation when navigating to a new dashboard
    if (location.pathname !== lastPathRef.current) {
      // Only glow on dashboard pages, not home
      if (location.pathname.includes('/dashboards/') || location.pathname.includes('/playbook/')) {
        setIsGlowing(true);
        // Stop glowing after 3 seconds
        const timer = setTimeout(() => setIsGlowing(false), 3000);
        return () => clearTimeout(timer);
      }
      lastPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Rotate prompts every 4 seconds when expanded
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setCurrentPromptIndex((prev) => (prev + 1) % prompts.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [isOpen, prompts.length]);

  // Focus input when expanded
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  // Handle submit
  const handleSubmit = () => {
    const message = inputValue.trim() || prompts[currentPromptIndex];
    if (message && onOpenChat) {
      onOpenChat(message);
      setInputValue('');
      onClose();
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Handle prompt click
  const handlePromptClick = (prompt) => {
    if (onOpenChat) {
      onOpenChat(prompt);
      onClose();
    }
  };

  // Don't render on certain pages
  if (location.pathname === '/login' || location.pathname === '/landing' || location.pathname === '/chat') {
    return null;
  }

  return (
    <Box
      position="fixed"
      top="32px"
      right="24px"
      zIndex={1000}
    >
      {/* Expanded Chat Input Panel */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          bg={bgColor}
          borderRadius="xl"
          boxShadow="xl"
          border="1px solid"
          borderColor={borderColor}
          p={4}
          mb={2}
          minW="320px"
          maxW="400px"
        >
          {/* Header */}
          <Flex justify="space-between" align="center" mb={3}>
            <HStack spacing={2}>
              <Flex
                w="28px"
                h="28px"
                borderRadius="lg"
                bgGradient="linear(to-br, blue.400, purple.500)"
                align="center"
                justify="center"
              >
                <Icon as={LuSparkles} color="white" boxSize={3.5} />
              </Flex>
              <Text fontWeight="600" fontSize="md" color={textColor}>
                AI Assistant
              </Text>
            </HStack>
            <IconButton
              icon={<LuX />}
              size="sm"
              variant="ghost"
              borderRadius="lg"
              onClick={onClose}
              aria-label="Close"
            />
          </Flex>

          {/* Quick Prompts */}
          <VStack spacing={2} mb={3} align="stretch">
            <Text fontSize="xs" color="gray.500" fontWeight="500">
              Try asking:
            </Text>
            {prompts.map((prompt, idx) => (
              <Box
                key={idx}
                as="button"
                onClick={() => handlePromptClick(prompt)}
                p={2.5}
                borderRadius="lg"
                bg={promptBg}
                textAlign="left"
                fontSize="sm"
                color={textColor}
                transition="all 0.2s"
                border="1px solid"
                borderColor="transparent"
                _hover={{
                  bg: promptHoverBg,
                  borderColor: 'blue.200',
                  transform: 'translateX(2px)',
                }}
              >
                {prompt}
              </Box>
            ))}
          </VStack>

          {/* Input */}
          <HStack
            spacing={2}
            bg={promptBg}
            borderRadius="xl"
            px={3}
            py={2}
            border="1px solid"
            borderColor={borderColor}
            _focusWithin={{
              borderColor: 'blue.300',
              boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.15)',
            }}
          >
            <Input
              ref={inputRef}
              placeholder={prompts[currentPromptIndex]}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="unstyled"
              size="sm"
              fontSize="sm"
              _placeholder={{ color: placeholderColor }}
            />
            <IconButton
              icon={<LuSend />}
              size="sm"
              borderRadius="lg"
              bgGradient={inputValue.trim() ? "linear(to-br, blue.400, blue.600)" : undefined}
              bg={inputValue.trim() ? undefined : "gray.200"}
              color={inputValue.trim() ? "white" : "gray.400"}
              onClick={handleSubmit}
              aria-label="Send"
              _hover={{
                bgGradient: inputValue.trim() ? "linear(to-br, blue.500, blue.700)" : undefined,
                bg: inputValue.trim() ? undefined : "gray.300",
              }}
            />
          </HStack>
        </Box>
      </Collapse>

      {/* Floating Button */}
      <Tooltip 
        label="Ask AI about this dashboard" 
        placement="left" 
        hasArrow
        isDisabled={isOpen}
      >
        <Flex
          as="button"
          onClick={onToggle}
          w={isOpen ? "44px" : "52px"}
          h={isOpen ? "44px" : "52px"}
          borderRadius="full"
          bgGradient="linear(to-br, blue.400, purple.500)"
          align="center"
          justify="center"
          cursor="pointer"
          boxShadow="lg"
          border="2px solid"
          borderColor="white"
          transition="all 0.3s ease"
          animation={isGlowing ? `${glowPulse} 1.5s ease-in-out infinite` : 'none'}
          _hover={{
            transform: 'scale(1.1)',
            boxShadow: 'xl',
          }}
          _active={{
            transform: 'scale(0.95)',
          }}
          ml="auto"
        >
          <Icon 
            as={isOpen ? LuChevronDown : LuSparkles} 
            color="white" 
            boxSize={isOpen ? 5 : 6} 
            transition="all 0.2s"
          />
        </Flex>
      </Tooltip>
    </Box>
  );
}

export default FloatingChatButton;
